/** @format */

import {If, IfElse, withUploadHandler} from 'frontier-utilities';
import withDesignUploaderHandler from './with_design_uploader_handler';
import NumberFormat from 'react-number-format';
import DropOverlay from './drop_overlay';
import Design from './design';
import React, {useCallback, useEffect} from 'react';

// styles
import './container.scss';
import SuperAgent from 'superagent';

const DesignUploaderContainer = withDesignUploaderHandler(
    withUploadHandler(props => {
        const designTotalQty = props.designs_orders.reduce((totalQty, design) => {
            if (design.qty === null || design.qty === undefined) {
                return totalQty;
            }
            totalQty = totalQty + parseInt(design.qty);
            return totalQty;
        }, 0);

        // If any designs have 0 quantity we do not want them to be able to submit
        const isEveryDesignComplete = props.designs_orders.every(({qty}) => qty > 0);

        const totalDesigns = props.designs_orders.length;

        const isSaveDisabled =
            props.totalQty != designTotalQty ||
            totalDesigns !== props.designQty ||
            !isEveryDesignComplete;

        const deleteDesigns = useCallback(() => {
            return props.designs_orders.map(design => {
                return SuperAgent.delete('/account/designs/delete/' + design.id).set(
                    'accept',
                    'application/json'
                );
            });
        }, [props.designs_orders]);

        useEffect(() => {
            const handleBeforeUnload = () => {
                if (props.modified) {
                    Promise.all(deleteDesigns());
                }
            };
            window.addEventListener('beforeunload', handleBeforeUnload);
            // Cleanup the event listener on component unmount or when deleteDesigns changes.
            return () => {
                window.removeEventListener('beforeunload', handleBeforeUnload);
            };
        }, [deleteDesigns]);

        const sortString = (valuea, valueb, direction) => {
            if (direction == 'asc') {
                return valuea.toLowerCase() > valueb.toLowerCase() ? 1 : -1;
            }
            return valuea.toLowerCase() < valueb.toLowerCase() ? 1 : -1;
        };

        const sortNumber = (valuea, valueb, direction) => {
            if (direction == 'asc') {
                return valuea > valueb ? 1 : -1;
            }
            return valuea < valueb ? 1 : -1;
        };

        const sort = (ordera, orderb) => {
            const sort = !props.sort ? 'id' : props.sort;
            const direction = !props.direction ? 'desc' : props.direction;
            if (typeof ordera[sort] === 'string' || ordera[sort] instanceof String) {
                return sortString(ordera[sort], orderb[sort], direction);
            } else if (
                typeof ordera[sort] === 'number' ||
                typeof ordera[sort] === 'boolean' ||
                ordera[sort] instanceof Number
            ) {
                return sortNumber(ordera[sort], orderb[sort], direction);
            } else {
                console.log('I cannot sort by ' + sort); // eslint-disable-line
            }
        };

        return (
            <div className="design-uploader">
                {props.dropping && <DropOverlay />}
                <table className="table table-striped w-100">
                    <thead>
                        <tr>
                            <th
                                className="text-nowrap"
                                onClick={props.onToggleSort.bind(this, 'id', 'desc')}>
                                Design Id
                            </th>
                            <th
                                onClick={props.onToggleSort.bind(
                                    this,
                                    'filename_without_id',
                                    'desc'
                                )}>
                                Files ({props.designs_orders.length} of {props.designQty})
                            </th>
                            <th onClick={props.onToggleSort.bind(this, 'qty', 'desc')}>Quantity</th>
                            <th>&nbsp;</th>
                        </tr>
                    </thead>
                    <tbody>
                        {props.designs_orders.sort(sort).map((design, index) => (
                            <tr key={design.id}>
                                <td>{design.design_id}</td>
                                <td>
                                    <Design
                                        {...design}
                                        isModified={props.modified}
                                        onRemoveDesign={props.onRemoveDesign.bind(this, design.id)}
                                    />
                                </td>
                                <td style={{textAlign: 'right', position: 'relative'}}>
                                    <NumberFormat
                                        disabled={!props.modified}
                                        className="form-control form-control-sm"
                                        value={design.qty}
                                        thousandSeparator={true}
                                        onChange={props.onQtyChange.bind(this, design.id)}
                                    />
                                    {index === 0 && props.modified && (
                                        <button
                                            style={{
                                                fontSize: '14px',
                                                lineHeight: '0px',
                                                position: 'absolute',
                                                top: '2px',
                                                right: '10px'
                                            }}
                                            className="btn btn-link btn-sm p-0 ml-2"
                                            onClick={props.onAutofill}>
                                            Auto Fill
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {Object.keys(props.fileProgress ?? {}).map((filename, index) => {
                    const progress = props.fileProgress[filename];
                    if (progress.totalPercent < 100 && !progress.error) {
                        return (
                            <div className="file-progress__progress" key={`${filename}_uploading`}>
                                <p>File: {filename}</p>
                                <div>
                                    <div style={{width: `${progress.totalPercent}%`}} />
                                </div>
                                <span>{progress.totalPercent}%</span>
                            </div>
                        );
                    }

                    if (progress.converting && !progress.error) {
                        return (
                            <div
                                className="file-progress__processing"
                                key={`${filename}_uploading`}>
                                <p>
                                    <b>File:</b> {filename}
                                </p>
                                <i className="fa fa-spinner fa-spin" />
                                <span className="d-inline-block ms-3">Converting...</span>
                            </div>
                        );
                    }

                    if (progress.error) {
                        return (
                            <div className="file-progress__error" key={`${filename}_uploading`}>
                                <p>
                                    <b>File:</b> {filename}
                                </p>
                                <div className="alert alert-danger" role="alert">
                                    {progress.error}
                                </div>
                            </div>
                        );
                    }

                    return <></>;
                })}
                <div className="design-uploader__total d-flex align-items-center justify-content-between">
                    {props.modified && (
                        <button
                            className="p-0 m-0 color-accent-primary"
                            onClick={props.onClickAddFiles}>
                            <u>Add More Files</u>
                        </button>
                    )}
                    <div style={{fontSize: '24px'}}>
                        <b>
                            <NumberFormat
                                style={{
                                    color: `${designTotalQty === props.totalQty ? 'green' : 'red'}`
                                }}
                                value={designTotalQty ?? 0}
                                displayType="text"
                                thousandSeparator={true}
                            />{' '}
                            of{' '}
                            <NumberFormat
                                value={props.totalQty}
                                displayType="text"
                                thousandSeparator={true}
                            />{' '}
                        </b>
                        Total
                    </div>
                </div>
                {props.error && <div className="alert alert-danger mt-4">{props.error}</div>}
                <div className="row">
                    <IfElse condition={!props.modified}>
                        <div className="text-right mt-2">
                            <button className="btn btn-success" disabled="disabled">
                                Saved
                            </button>
                        </div>

                        <>
                            <div style={{color: 'red', fontSize: '18px', textAlign: 'right'}}>
                                Uploaded designs are not saved until you submit for printing
                            </div>
                            <If condition={isSaveDisabled}>
                                <div style={{textAlign: 'right'}}>
                                    <IfElse condition={parseInt(props.designQty) !== totalDesigns}>
                                        <span className="alert alert-warning">
                                            You have purchased {props.designQty} designs but have
                                            uploaded {totalDesigns}
                                        </span>
                                        <IfElse condition={!isEveryDesignComplete}>
                                            <span className="alert alert-warning">
                                                Some designs are missing a quantity
                                            </span>
                                            <span className="alert alert-warning">
                                                {props.totalQty > designTotalQty ? (
                                                    <span style={{fontWeight: 'bold'}}>Add</span>
                                                ) : (
                                                    <span style={{fontWeight: 'bold'}}>Remove</span>
                                                )}{' '}
                                                {Math.abs(props.totalQty - designTotalQty)} Qty to
                                                Save for Printing
                                            </span>
                                        </IfElse>
                                    </IfElse>
                                </div>
                            </If>
                            <div className="d-flex align-items-center justify-content-end">
                                <IfElse condition={!props.saving}>
                                    <button
                                        disabled={isSaveDisabled}
                                        className="btn btn--primary mt-2"
                                        onClick={props.onSaveChanges}>
                                        Submit for Printing
                                    </button>
                                    <button className="btn btn--primary" disabled>
                                        Saving...
                                    </button>
                                </IfElse>
                            </div>
                        </>
                    </IfElse>
                </div>
            </div>
        );
    })
);

export default DesignUploaderContainer;
